import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import MultiWebsiteSelector from '../../../../../shared/components/analytics/MultiWebsiteSelector';
import ActivityContractSelector from '../../../../../shared/components/analytics/ActivityContractSelector';
import { getDefaultDateRange } from '../../../../../shared/utils/dateUtils';
import axiosInstance from '../../../../../shared/services/api/axiosInstance';

const RetentionFilterPopup = ({ isOpen, onClose, onFiltersChange, appliedFilters, teamId }) => {
  // Filter state
  const [selectedWebsites, setSelectedWebsites] = useState(appliedFilters?.websites || []);
  const [selectedContracts, setSelectedContracts] = useState(appliedFilters?.contracts || []);
  const [dataMode, setDataMode] = useState(appliedFilters?.dataMode || 'users'); // 'users' or 'wallets'
  const [dateRange] = useState(getDefaultDateRange());
  const hasInitializedRef = useRef(false);

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Fetch websites and contracts, and set default selections (first item only)
  useEffect(() => {
    if (!isOpen || !teamId) return;

    const fetchAndSetDefaults = async () => {
      // Fetch websites for users mode
      if (dataMode === 'users') {
        // Only set default if no websites are currently selected
        if (selectedWebsites.length === 0 && (!appliedFilters?.websites || appliedFilters.websites.length === 0)) {
          try {
            const cacheKey = `websites_${teamId}`;
            const cached = sessionStorage.getItem(cacheKey);
            let websites = null;

            if (cached) {
              try {
                const { data, timestamp } = JSON.parse(cached);
                if (Date.now() - timestamp < 5 * 60 * 1000) {
                  websites = data;
                }
              } catch (e) {
                console.warn('Failed to parse cached websites:', e);
              }
            }

            if (!websites) {
              const isNumericId = typeof teamId === 'number' || (!isNaN(teamId) && Number.isInteger(Number(teamId)));
              let response;
              if (isNumericId) {
                response = await axiosInstance.get(`/website/team/${teamId}`);
              } else {
                response = await axiosInstance.get(`/website/name/${encodeURIComponent(teamId)}`);
              }
              websites = response.data?.websites || response.data || [];
              
              sessionStorage.setItem(cacheKey, JSON.stringify({
                data: websites,
                timestamp: Date.now()
              }));
            }

            const activeWebsites = websites.filter(w => w.is_verified || w.isVerified);
            if (activeWebsites.length > 0) {
              // Select only the first website
              const firstSiteId = activeWebsites[0].site_id || activeWebsites[0].siteId;
              setSelectedWebsites([firstSiteId]);
            }
          } catch (error) {
            console.error('Error fetching websites for default selection:', error);
          }
        }
      }

      // Fetch contracts for wallets mode
      if (dataMode === 'wallets') {
        // Only set default if no contracts are currently selected
        if (selectedContracts.length === 0 && (!appliedFilters?.contracts || appliedFilters.contracts.length === 0)) {
          try {
            let teamName = teamId;
            if (typeof teamId === 'number' || !isNaN(teamId)) {
              teamName = localStorage.getItem('selectedTeam') || teamId;
            }
            
            const response = await axiosInstance.get(`/smart-contracts/team/${encodeURIComponent(teamName)}`);
            
            if (response.data?.contracts) {
              const contracts = response.data.contracts.map(contract => ({
                id: contract.contractId || contract.id,
                address: contract.address || contract.contract_address,
                name: contract.name || contract.contract_name || `Contract ${(contract.address || contract.contract_address || '').substring(0, 6)}...${(contract.address || contract.contract_address || '').substring((contract.address || contract.contract_address || '').length - 4)}`,
                blockchain: contract.blockchain || contract.chain,
                tokenSymbol: contract.tokenSymbol || contract.symbol || contract.contract_symbol,
                contractType: contract.contractType || 'main'
              }));

              if (contracts.length > 0) {
                // Select only the first contract
                setSelectedContracts([contracts[0]]);
              }
            }
          } catch (error) {
            console.error('Error fetching contracts for default selection:', error);
          }
        }
      }
    };

    fetchAndSetDefaults();
  }, [isOpen, teamId, dataMode, selectedWebsites.length, selectedContracts.length]);

  // Sync with applied filters when popup opens (but don't override if we already have selections)
  useEffect(() => {
    if (isOpen && appliedFilters) {
      // Only sync if we don't already have selections (to avoid overriding our default first-item selection)
      if (appliedFilters.websites && appliedFilters.websites.length > 0) {
        setSelectedWebsites(appliedFilters.websites);
      }
      if (appliedFilters.contracts && appliedFilters.contracts.length > 0) {
        setSelectedContracts(appliedFilters.contracts);
      }
      setDataMode(appliedFilters.dataMode || 'users');
    }
  }, [isOpen, appliedFilters]);

  const handleApplyFilters = () => {
    const filters = {
      websites: dataMode === 'users' ? selectedWebsites : [], // Only include websites in users mode
      contracts: dataMode === 'wallets' ? selectedContracts : [], // Only include contracts in wallets mode
      dataMode: dataMode,
      sessions: [], // Keep empty for compatibility
      userIdentities: [], // Keep empty for compatibility
      dateRange
    };
    
    onFiltersChange?.(filters);
    onClose();
  };

  const handleResetFilters = () => {
    setSelectedWebsites([]);
    setSelectedContracts([]);
    setDataMode('users');
  };

  const hasActiveFilters = () => {
    if (dataMode === 'users') {
      return selectedWebsites.length > 0;
    } else {
      return selectedContracts.length > 0;
    }
  };

  // Data Mode Toggle Component (Users/Wallets)
  const DataModeToggle = ({ value, onChange }) => (
    <div className="flex border overflow-hidden" style={{ borderColor: styles.borderColor }}>
      <button
        onClick={() => onChange('users')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'users' ? styles.accentColor : styles.primaryBg,
          color: value === 'users' ? '#ffffff' : styles.textPrimary
        }}
      >
        Users
      </button>
      <button
        onClick={() => onChange('wallets')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'wallets' ? styles.accentColor : styles.primaryBg,
          color: value === 'wallets' ? '#ffffff' : styles.textPrimary
        }}
      >
        Wallets
      </button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white border border-gray-200 w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl rounded-lg" style={{ overflow: 'visible' }}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0" style={{ zIndex: 1 }}>
          <h2 className="text-lg font-semibold text-[#0f172a]" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Retention Filters
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 text-[#475569] hover:text-[#475569] transition-colors rounded"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1" style={{ overflow: 'visible' }}>
          {/* Data Mode Toggle */}
          <div className="px-6 py-4 border-b border-gray-200">
            <label className="block text-sm font-medium text-[#475569] mb-2" style={{ fontFamily: 'Raleway, sans-serif' }}>
              Data Mode
            </label>
            <DataModeToggle value={dataMode} onChange={(newMode) => {
              setDataMode(newMode);
              // Clear selections when switching modes
              setSelectedWebsites([]);
              setSelectedContracts([]);
            }} />
          </div>

          {/* Filter Section - Simplified based on mode */}
          <div className="px-6 py-4 border-b border-gray-200" style={{ position: 'relative', zIndex: 10 }}>
            {/* Websites - Only show in Users mode */}
            {dataMode === 'users' && (
              <div style={{ position: 'relative', zIndex: 100 }}>
                <label className="block text-sm font-medium text-[#475569] mb-2" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Websites
                </label>
                <MultiWebsiteSelector
                  selectedSiteIds={selectedWebsites}
                  onSiteChange={setSelectedWebsites}
                  teamId={teamId}
                  placeholder="Select websites"
                  showLabel={false}
                  selectFirstOnly={true}
                />
              </div>
            )}
            
            {/* Smart Contracts - Only show in Wallets mode */}
            {dataMode === 'wallets' && (
              <div style={{ position: 'relative', zIndex: 100 }}>
                <label className="block text-sm font-medium text-[#475569] mb-2" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Smart Contracts
                </label>
                <ActivityContractSelector
                  selectedContracts={selectedContracts}
                  onContractChange={setSelectedContracts}
                  teamId={teamId}
                  placeholder="Select smart contracts"
                  showLabel={false}
                  selectFirstOnly={true}
                />
              </div>
            )}
          </div>

        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 flex-shrink-0" style={{ zIndex: 1 }}>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 text-sm font-medium text-[#475569] hover:text-[#0f172a] transition-colors"
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            Reset All
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#475569] border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              Cancel
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 text-sm font-medium text-white bg-[#1d0c46] rounded-md hover:bg-[#1d0c46]/90 transition-colors"
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              Apply Filters {hasActiveFilters() && '•'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetentionFilterPopup;

